import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, Scale, Shield } from 'lucide-react';
import { OrgaConfigurationRepository } from '@/services/firebase/orga-configuration-repository';
import { OrgaConfiguration } from '@/models/orgaConfiguration';
import { generateLegalDocuments } from '@/utils/legalDocumentsGenerator';

type LegalSection = 'cgv' | 'mentions' | 'privacy';

interface Personalization {
    card: { background: string; border: string; borderRadius: string; shadow: string };
    text: { primary: string; secondary: string; heading: string };
    button: { primary: string; primaryText: string };
    icon: { primary: string };
}

const defaultPersonalization: Personalization = {
    card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-2xl', shadow: 'shadow-lg' },
    text: { primary: '#1F2937', secondary: '#6B7280', heading: '#111827' },
    button: { primary: '#9333EA', primaryText: '#FFFFFF' },
    icon: { primary: '#9333EA' },
};

/**
 * Simple Markdown renderer for legal documents
 */
const MarkdownRenderer: React.FC<{ content: string; style?: React.CSSProperties }> = ({ content, style }) => {
    // Basic markdown parsing logic
    const sections = content.split('\n');

    return (
        <div className="space-y-4 text-sm leading-relaxed" style={style}>
            {sections.map((line, index) => {
                const trimmedLine = line.trim();

                if (!trimmedLine) return <div key={index} className="h-2" />;

                // H1 headers (# Title)
                if (trimmedLine.startsWith('# ')) {
                    return (
                        <h1 key={index} className="text-2xl font-bold mb-4 pt-2 border-b pb-2">
                            {trimmedLine.replace('# ', '')}
                        </h1>
                    );
                }

                // H2 headers (## Title)
                if (trimmedLine.startsWith('## ')) {
                    return (
                        <h2 key={index} className="text-lg font-bold mt-6 mb-2">
                            {trimmedLine.replace('## ', '')}
                        </h2>
                    );
                }

                // Unordered list items (- Item)
                if (trimmedLine.startsWith('- ')) {
                    const content = parseInlineMarkdown(trimmedLine.replace('- ', ''));
                    return (
                        <div key={index} className="flex gap-2 ml-4 mb-1">
                            <span className="text-gray-400">•</span>
                            <span>{content}</span>
                        </div>
                    );
                }

                // Warning section (starts with emoji)
                if (trimmedLine.startsWith('⚠️') || trimmedLine.startsWith('✅')) {
                    return (
                        <div key={index} className="p-3 bg-yellow-50/50 rounded-lg border border-yellow-100 my-4 text-xs italic">
                            {parseInlineMarkdown(trimmedLine)}
                        </div>
                    );
                }

                // Standard paragraph
                return (
                    <p key={index} className="mb-2">
                        {parseInlineMarkdown(trimmedLine)}
                    </p>
                );
            })}
        </div>
    );
};

/**
 * Helper to parse bold text within a line
 */
const parseInlineMarkdown = (text: string) => {
    // Regex for **bold**
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
        }
        return part;
    });
};


const LegalPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { idorga } = useParams<{ idorga: string }>();
    const [searchParams] = useSearchParams();
    const [configuration, setConfiguration] = useState<OrgaConfiguration | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState<LegalSection>('cgv');

    // Generate legal documents based on configuration and current language
    const legalDocuments = useMemo(() => {
        if (!configuration) return null;
        const language = i18n.language.startsWith('es') ? 'es' : i18n.language.startsWith('en') ? 'en' : 'fr';
        return generateLegalDocuments(configuration, language);
    }, [configuration, i18n.language]);

    useEffect(() => {
        const section = searchParams.get('section') as LegalSection;
        if (section && ['cgv', 'mentions', 'privacy'].includes(section)) {
            setActiveSection(section);
        }
    }, [searchParams]);

    useEffect(() => {
        const loadConfiguration = async () => {
            if (!idorga) return;

            try {
                setLoading(true);
                const config = await OrgaConfigurationRepository.getByOrganizationId(idorga);
                setConfiguration(config);
            } catch (error) {
                console.error('Error loading configuration:', error);
            } finally {
                setLoading(false);
            }
        };

        loadConfiguration();
    }, [idorga]);

    // Build personalization with safe defaults
    const personalization: Personalization = {
        card: {
            background: configuration?.personalization?.card?.background || defaultPersonalization.card.background,
            border: configuration?.personalization?.card?.border || defaultPersonalization.card.border,
            borderRadius: configuration?.personalization?.card?.borderRadius || defaultPersonalization.card.borderRadius,
            shadow: configuration?.personalization?.card?.shadow || defaultPersonalization.card.shadow,
        },
        text: {
            primary: configuration?.personalization?.text?.primary || defaultPersonalization.text.primary,
            secondary: configuration?.personalization?.text?.secondary || defaultPersonalization.text.secondary,
            heading: configuration?.personalization?.text?.heading || defaultPersonalization.text.heading,
        },
        button: {
            primary: configuration?.personalization?.button?.primary || defaultPersonalization.button.primary,
            primaryText: configuration?.personalization?.button?.primaryText || defaultPersonalization.button.primaryText,
        },
        icon: {
            primary: configuration?.personalization?.icon?.primary || defaultPersonalization.icon.primary,
        },
    };

    const getSectionContent = () => {
        if (!legalDocuments) return { title: '', icon: null, content: t('legal.noContent') };

        switch (activeSection) {
            case 'cgv':
                return {
                    title: t('legal.sections.termsAndConditions'),
                    icon: <FileText className="w-6 h-6" />,
                    content: legalDocuments.termsAndConditions,
                };
            case 'mentions':
                return {
                    title: t('legal.sections.legalNotice'),
                    icon: <Scale className="w-6 h-6" />,
                    content: legalDocuments.legalNotice,
                };
            case 'privacy':
                return {
                    title: t('legal.sections.privacyPolicy'),
                    icon: <Shield className="w-6 h-6" />,
                    content: legalDocuments.privacyPolicy,
                };
            default:
                return { title: '', icon: null, content: '' };
        }
    };

    const sections: { key: LegalSection; label: string; icon: React.ReactNode; available: boolean }[] = [
        {
            key: 'cgv',
            label: t('legal.sections.termsAndConditions'),
            icon: <FileText className="w-4 h-4" />,
            available: !!legalDocuments,
        },
        {
            key: 'mentions',
            label: t('legal.sections.legalNotice'),
            icon: <Scale className="w-4 h-4" />,
            available: !!legalDocuments,
        },
        {
            key: 'privacy',
            label: t('legal.sections.privacyPolicy'),
            icon: <Shield className="w-4 h-4" />,
            available: !!legalDocuments,
        },
    ];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9FAFB' }}>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: personalization.button.primary }}></div>
            </div>
        );
    }

    if (!configuration) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9FAFB' }}>
                <div className="text-center p-8">
                    <h1 className="text-2xl font-bold text-gray-800 mb-4">{t('legal.organizationNotFound')}</h1>
                    <p className="text-gray-600">{t('legal.organizationNotFoundDescription')}</p>
                </div>
            </div>
        );
    }

    const currentSection = getSectionContent();

    return (
        <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F9FAFB' }}>
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <Link
                        to={`/orga/${idorga}/order`}
                        className="inline-flex items-center gap-2 text-sm font-medium mb-4 hover:opacity-80 transition-opacity"
                        style={{ color: personalization.button.primary }}
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('legal.backToOrder')}
                    </Link>

                    <div className="flex items-center gap-4">
                        {configuration.personalization?.logo && (
                            <img
                                src={configuration.personalization.logo}
                                alt={configuration.name}
                                className="w-12 h-12 object-contain rounded-lg"
                            />
                        )}
                        <div>
                            <h1 className="text-2xl font-bold" style={{ color: personalization.text.heading }}>
                                {configuration.name}
                            </h1>
                            <p className="text-sm" style={{ color: personalization.text.secondary }}>
                                {t('legal.title')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Navigation tabs */}
                <div
                    className="flex flex-wrap gap-2 mb-6 p-2 rounded-xl"
                    style={{ backgroundColor: personalization.card.background }}
                >
                    {sections.filter(s => s.available).map((section) => (
                        <button
                            key={section.key}
                            onClick={() => setActiveSection(section.key)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeSection === section.key ? 'shadow-sm' : 'hover:opacity-80'
                                }`}
                            style={{
                                backgroundColor: activeSection === section.key ? personalization.button.primary : 'transparent',
                                color: activeSection === section.key ? personalization.button.primaryText : personalization.text.primary,
                            }}
                        >
                            {section.icon}
                            {section.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div
                    className="rounded-2xl shadow-lg p-6 md:p-8"
                    style={{
                        backgroundColor: personalization.card.background,
                        borderColor: personalization.card.border,
                        borderWidth: '1px',
                        borderStyle: 'solid',
                    }}
                >
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b" style={{ borderColor: personalization.card.border }}>
                        <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${personalization.button.primary}20`, color: personalization.button.primary }}
                        >
                            {currentSection.icon}
                        </div>
                        <h2 className="text-xl font-bold" style={{ color: personalization.text.heading }}>
                            {currentSection.title}
                        </h2>
                    </div>

                    <MarkdownRenderer
                        content={currentSection.content}
                        style={{ color: personalization.text.primary }}
                    />
                </div>

                {/* Company info */}
                {configuration.legalInfo?.companyName && (
                    <div
                        className="mt-6 p-4 rounded-xl text-sm"
                        style={{
                            backgroundColor: personalization.card.background,
                            borderColor: personalization.card.border,
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            color: personalization.text.secondary,
                        }}
                    >
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {configuration.legalInfo.companyName && (
                                <div>
                                    <span className="font-medium" style={{ color: personalization.text.heading }}>
                                        {t('legal.companyInfo.companyName')}
                                    </span>
                                    <p>{configuration.legalInfo.companyName}</p>
                                </div>
                            )}
                            {configuration.legalInfo.legalId && (
                                <div>
                                    <span className="font-medium" style={{ color: personalization.text.heading }}>
                                        {t('legal.companyInfo.legalId')}
                                    </span>
                                    <p>{configuration.legalInfo.legalId}</p>
                                </div>
                            )}
                            {configuration.legalInfo.vatNumber && (
                                <div>
                                    <span className="font-medium" style={{ color: personalization.text.heading }}>
                                        {t('legal.companyInfo.vatNumber')}
                                    </span>
                                    <p>{configuration.legalInfo.vatNumber}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LegalPage;
